import {useLocation, useNavigate, useParams} from "react-router";
import Navbar from "../components/Navbar/Navbar";
import {useTranslation} from "react-i18next";
import {mustache} from "../Helpers/functions";
import {Alert, Col, Container, Row} from "react-bootstrap";
import style from "../components/PaymentItems/Payment.module.css";
import { useState, useEffect } from "react";
import Button from 'react-bootstrap/Button';
import postCreatePrimotechSession from "../Api/Requests/post-create-primotech-session";
import postInitiatePrimotechPayment from "../Api/Requests/post-initiate-primotech-payment";
import postConfirmPrimotechOtp from "../Api/Requests/post-confirm-primotech-otp";
import i18n from "../i18n";
import StepOne from "../components/Primotech/StepOne";
import StepTwo from "../components/Primotech/StepTwo";
import StepThree from "../components/Primotech/StepThree";
import "../components/Primotech/Primotech.css";

const Primotech = () => {
    const location = useLocation();
    const dataLocation = location.state
    const {t} = useTranslation();
    let {memberId, cartId, identification, paymentSecret} = useParams();
    const navigate = useNavigate();

    // Step tracking
    const [step, setStep] = useState(1);
    const [sessionToken, setSessionToken] = useState('');
    const [transactionId, setTransactionId] = useState('');

    // Form inputs
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');

    // UI states
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Step 1: Create session on component mount
    useEffect(() => {
        createSession();
    }, []);

    const createSession = () => {
        setIsLoading(true);
        setHasError(false);
        const data = {
            memberId,
            cartId,
            identification,
            paymentSecret
        }

        postCreatePrimotechSession(data).then((response) => {
            if (response.data?.token) {
                setSessionToken(response.data.token);
                setStep(2);
                setIsLoading(false);
            } else {
                setHasError(true);
                setErrorMessage(t('primotech.step1.errorMessage') || 'Unable to create payment session. Please try again.');
                setIsLoading(false);
            }
        }).catch((error) => {
            setHasError(true);
            setErrorMessage(t('primotech.step1.errorMessage') || 'Unable to create payment session. Please try again.');
            setIsLoading(false);
        })
    }

    const validatePhoneNumber = (phone: string): boolean => {
       return /^0?9\d{8}$/.test(phone);
    }

    const initiatePayment = () => {
        if (!validatePhoneNumber(phoneNumber)) {
            setHasError(true);
            setErrorMessage(t('primotech.invalidPhone') || 'Invalid phone number');
            return;
        }

        setIsLoading(true);
        setHasError(false);
        const data = {
            memberId,
            cartId,
            identification,
            mobile: phoneNumber,
            token : sessionToken,
            paymentSecret
        }

        postInitiatePrimotechPayment(data).then((response) => {
            if (response.data?.transactionId) {
                setTransactionId(response.data.transactionId);
                setStep(3);
                setIsLoading(false);
                setPhoneNumber('');
            } else {
                setHasError(true);
                setErrorMessage(t('primotech.paymentInitError') || 'Failed to initiate payment');
                setIsLoading(false);
            }
        }).catch((error) => {
            setHasError(true);
            setErrorMessage(error.response?.data?.message || t('primotech.paymentInitError') || 'Failed to initiate payment');
            setIsLoading(false);
        })
    }

    const validateOtp = (otpValue: string): boolean => {
        return /^\d{6}$/.test(otpValue);
    }

    const confirmOtp = () => {
        if (!validateOtp(otp)) {
            setHasError(true);
            setErrorMessage(t('primotech.invalidOtp') || 'OTP must be 6 digits');
            return;
        }

        setIsLoading(true);
        setHasError(false);
        const data = {
            memberId,
            cartId,
            identification,
            otp,
            transactionId,
            token : sessionToken,
            paymentSecret
        }

        postConfirmPrimotechOtp(data).then((response) => {
            if (response.data?.redirectTo) {
                // Check if backend provides a redirect URL
                console.log(11111, response.data.redirectTo)
                if (response.data?.redirectTo) {
                    window.location.href = response.data.redirectTo;
                } else {
                    navigate(`/payment/screen/${paymentSecret}/${cartId}`);
                }
            } else {
                setHasError(true);
                setErrorMessage(response.data?.result?.message || t('primotech.otpError') || 'Failed to confirm OTP');
                setIsLoading(false);
            }
        }).catch((error) => {
            setHasError(true);
            const errorMessage = error.response?.data?.message || error.response?.data?.result?.message || t('primotech.otpError') || 'Failed to confirm OTP';
            setErrorMessage(errorMessage);
            setIsLoading(false);
            
            // Reset OTP input on 500 error
            if (error.response?.status === 500) {
                setOtp('');
            }
        })
    }

    return (
        <>
        <div dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
        <div className={`${style.blocked_background}`} id={'background-popup'}></div>
        <Container className={`sm-pad-zero`}>
            <Row className={`justify-content-center`}>
                <Col className={`${style.parent_container}`} lg={6} md={8} sm={10}>
                    <Navbar 
                        referral_url={''} 
                        loading={false} 
                        title={`${t('primotech.payUsingPrimotech') || 'Pay with PrimoTech'} - ${t('primotech.step') || 'Step'} ${step}/3`}
                        subTitle={mustache(t('page.payment.subtitle'), {cartId: dataLocation?.cartId})} 
                    />
                    <div className="primotech-main-content">
                        {step === 1 && (
                            <Alert key={'danger'} variant={'danger'} className="primotech-alert" show={hasError}>
                                {errorMessage}
                            </Alert>
                        )}
                        {step === 1 && hasError && (
                            <Button 
                                variant="primary" 
                                type="button" 
                                onClick={createSession}
                                disabled={isLoading}
                                className="primotech-retry-button"
                            >
                                {!isLoading && (t('primotech.tryAgain') || 'Try Again')}
                                {isLoading && (t('primotech.button.wait') || 'Please wait...')}
                            </Button>
                        )}

                        {step === 1 && !hasError && (
                            <StepOne 
                                isLoading={isLoading}
                                hasError={hasError}
                                onRetry={createSession}
                            />
                        )}

                        {step === 2 && (
                            <StepTwo 
                                step={step}
                                phoneNumber={phoneNumber}
                                onPhoneChange={(value) => {
                                    setPhoneNumber(value);
                                    setHasError(false);
                                }}
                                onSubmit={initiatePayment}
                                isLoading={isLoading}
                                hasError={hasError}
                                errorMessage={errorMessage}
                            />
                        )}

                        {step === 3 && (
                            <StepThree
                                step={step}
                                otp={otp}
                                onOtpChange={(value) => {
                                    setOtp(value);
                                    setHasError(false);
                                }}
                                onSubmit={confirmOtp}
                                isLoading={isLoading}
                                hasError={hasError}
                                errorMessage={errorMessage}
                            />
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    </div>
    <style>{`
        @media (max-width: 767px) {
            .primotech-desktop-progress {
                display: none !important;
            }
        }
        
        @media (min-width: 768px) {
            .primotech-mobile-tabs {
                display: none !important;
            }
        }
    `}</style>
    </>)
}

export default Primotech
